import React, { useState } from 'react';
import {
  exportMapAsPng, exportGeoJSON, exportFeaturesAsCSV, generateShareUrl,
  collectVisibleFeatures, collectCsvFeatures, copyToClipboard,
} from '../lib/export';
import { useToast } from '../components/Toast';
import { describeSkipped } from '../lib/xyz-geojson';
import ShareLinkDialog from '../components/map/ShareLinkDialog';

/**
 * File import dialog, external service layers and the ribbon's Share-tab
 * export handlers (PNG / GeoJSON / CSV / copy link) for MapView.
 *
 * GeoJSON exports every visible vector layer as one FeatureCollection (`layer` property per
 * feature); CSV exports the active layer, or all visible point features. Copy Link builds the
 * URL from the live map view and keeps the current search params; hooks/useMapViewPersistence
 * restores the view from its hash.
 */
export function useImportExport({
  mapRefHolder, viewport, layers, setImportedLayers, setProjectCenter,
  importedLayers = [], resultLayers = [], usgsData = {}, activeLayerKey = null,
}) {
  const { showToast } = useToast();
  const [showFileImport, setShowFileImport] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [serviceLayers, setServiceLayers] = useState([]);
  const [shareUrl, setShareUrl] = useState(null);

  const handleImport = (geojson, name, type) => {
    const xyz = geojson.metadata?.xyz || null;
    setImportedLayers(prev => [...prev, {
      id: `import-${Date.now()}`, name, geojson, visible: true,
      xyz, activeZ: xyz?.activeZ || null,
    }]);
    setShowFileImport(false);
    // V.46: report rows dropped for missing/invalid coordinates (also in the layer's Info tab)
    const skipped = describeSkipped(xyz);
    const n = geojson.features.length;
    showToast(`Imported ${n.toLocaleString()} feature${n === 1 ? '' : 's'} from ${name}${skipped ? `; ${skipped}` : ''}`,
      skipped ? 'warning' : 'success', skipped ? 8000 : 3000);
    // Zoom to imported data
    const coords = geojson.features.filter(f => f.geometry?.type === 'Point').map(f => f.geometry.coordinates);
    if (coords.length > 0) {
      const lons = coords.map(c => c[0]), lats = coords.map(c => c[1]);
      setProjectCenter({
        lat: (Math.min(...lats.slice(0, 5000)) + Math.max(...lats.slice(0, 5000))) / 2,
        lon: (Math.min(...lons.slice(0, 5000)) + Math.max(...lons.slice(0, 5000))) / 2,
        zoom: 13,
      });
    }
  };

  const addRasterLayer = (name, tileUrl) => {
    setServiceLayers(prev => [...prev, { id: `svc-${Date.now()}`, name, type: 'raster', tileUrl, visible: true }]);
  };
  const addVectorLayer = (name, geojson) => {
    setServiceLayers(prev => [...prev, { id: `svc-${Date.now()}`, name, type: 'vector', geojson, visible: true }]);
  };

  const sources = { importedLayers, resultLayers, layers, usgsData };

  // The live view comes from the map itself; the debounced `viewport` state is only a fallback.
  const currentView = () => {
    const map = mapRefHolder.current?.current?.getMap?.();
    if (!map) return viewport;
    const c = map.getCenter();
    return { latitude: c.lat, longitude: c.lng, zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
  };

  const exportHandlers = {
    onExportPng: () => exportMapAsPng(mapRefHolder.current),
    onExportGeoJSON: () => {
      const fc = collectVisibleFeatures(sources);
      if (!fc.features.length) { showToast('Nothing to export: no visible vector layers have features', 'warning'); return; }
      exportGeoJSON(fc, 'ultimate-gis-export.geojson');
      showToast(`Exported ${fc.features.length} feature${fc.features.length === 1 ? '' : 's'} as GeoJSON`, 'success');
    },
    onExportCSV: () => {
      const { features, name } = collectCsvFeatures({ ...sources, activeLayerKey });
      if (!features.length) { showToast('Nothing to export: no active layer or visible points', 'warning'); return; }
      const file = name ? `${name.replace(/[^\w.-]+/g, '_')}.csv` : 'ultimate-gis-export.csv';
      exportFeaturesAsCSV(features, file);
      showToast(`Exported ${features.length} feature${features.length === 1 ? '' : 's'} as CSV`, 'success');
    },
    onCopyLink: async () => {
      const url = generateShareUrl(currentView(), layers.filter(l => l.visible).map(l => l.id));
      if (await copyToClipboard(url)) showToast('Link copied to clipboard', 'success');
      else setShareUrl(url);
    },
  };

  const shareElement = shareUrl ? (
    <ShareLinkDialog
      url={shareUrl}
      onClose={() => setShareUrl(null)}
      onCopy={async (u) => {
        const ok = await copyToClipboard(u);
        if (ok) showToast('Link copied to clipboard', 'success');
        return ok;
      }}
    />
  ) : null;

  return {
    showFileImport, setShowFileImport, showServiceDialog, setShowServiceDialog,
    serviceLayers, setServiceLayers, handleImport, addRasterLayer, addVectorLayer, exportHandlers, shareElement,
  };
}
